/**
 * Display helpers ported from the design's `lib/format.ts`.
 *
 * Only the ones that carry no money in them live here: the app's amounts are
 * BigInt micros handled by `@empite/api-client`'s money module, and the
 * design's number formatting is written against plain JS numbers.
 */

/**
 * Middle-ellipsis for an opaque identifier. The full value still has to be
 * passed to anything that copies it — this is for the eye only.
 */
export function truncateId(id: string, head = 8, tail = 12) {
  if (id.length <= head + tail + 1) return id;
  return `${id.slice(0, head)}…${id.slice(-tail)}`;
}

/** `pending_phone_verification` → `pending phone verification`. */
export function humanizeState(value: string) {
  return value.replace(/_/g, " ");
}

/**
 * Timestamps are split by hand rather than parsed into a `Date`, exactly as
 * the design does it: `toLocaleString` renders differently per machine locale
 * and time zone, so a table would read one way in a screenshot and another on
 * a colleague's laptop.
 *
 * Unlike the design's fixtures, which held bare `2026-08-25T15:25:44` strings,
 * the platform returns RFC3339 WITH a zone (`…44Z`, `…44+10:00`). The zone
 * suffix is stripped rather than applied — the fields are displayed as the
 * server wrote them.
 */
function parts(iso: string) {
  const [date = "", rest = "00:00:00"] = iso.split("T");
  const [y = "", m = "01", d = "01"] = date.split("-");
  const time = rest.replace(/(Z|[+-]\d{2}:?\d{2})$/i, "");
  const [hh = "00", mm = "00", ss = "00"] = time.split(":");
  return { y, m, d, hh, mm, ss: ss.split(".")[0] ?? "00" };
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** `25/08/2026, 15:25:44` — the format the design uses in every table. */
export function formatTimestamp(iso: string) {
  const { y, m, d, hh, mm, ss } = parts(iso);
  return `${d}/${m}/${y}, ${hh}:${mm}:${ss}`;
}

/** `25 Aug 2026, 15:25` — friendlier, for cards rather than tables. */
export function formatDateTime(iso: string) {
  const { y, m, d, hh, mm } = parts(iso);
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}, ${hh}:${mm}`;
}

/** `25 Aug 2026` — a date with no clock on it, for a deadline the customer is
 *  told about rather than an event that happened at a moment. */
export function formatDate(iso: string) {
  const { y, m, d } = parts(iso);
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
}

/** `25 Aug` — chart axes. */
export function formatDayMonth(iso: string) {
  const { m, d } = parts(iso);
  return `${Number(d)} ${MONTHS[Number(m) - 1]}`;
}

/**
 * `1,204` — request counts and other plain tallies. The locale is PINNED to
 * `en-US` rather than left to the machine, for the same reason the timestamp
 * helpers are hand-rolled: a number that groups with dots on one laptop and
 * commas on another makes two screenshots of the same screen disagree.
 *
 * Counts are plain JS numbers — unlike money, which is BigInt micros and is
 * formatted by `@empite/api-client` instead.
 */
export function formatCount(value: number) {
  return value.toLocaleString("en-US");
}

/**
 * `25%` — a proportion the platform sent in BASIS POINTS.
 *
 * The arithmetic is deliberately not done here: 2500 arrives already computed
 * and already truncated toward zero by the platform, so a plan is never
 * advertised as better than it is, and the browser never divides one amount of
 * money by another. All this does is move the decimal point and drop the
 * trailing zeros, so 2500 reads `25%` rather than `25.00%` while 1666 keeps
 * the two places it needs.
 */
export function formatBps(bps: number) {
  return `${String(Math.trunc(bps) / 100)}%`;
}

/**
 * `2 hrs ago` — for "last seen" and "asked to join" lines.
 *
 * `now` stays an explicit argument, as it is in the design, so a component
 * that renders a list of these formats them all against ONE instant instead
 * of drifting mid-render. It defaults to the clock for callers that have
 * nothing better to pass.
 *
 * The design appended a literal `Z` before parsing because its fixtures held
 * zoneless strings; the platform's timestamps carry their own offset, so they
 * are parsed as written — adding a `Z` here would make `…+10:00` unparseable.
 * A future instant reads as "just now" rather than growing an "in …" case,
 * which is the design's behaviour and all any current caller needs.
 */
export function relativeTime(iso: string, now: string | number = Date.now()) {
  const then = Date.parse(iso);
  const reference = typeof now === "number" ? now : Date.parse(now);
  const mins = Math.round((reference - then) / 60000);
  if (!Number.isFinite(mins) || mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}
